/* ==========================================================
   Arma tu pack — custom element
   Cada instancia es independiente: puedes tener varios bloques
   en la misma página sin que se pisen.
   ========================================================== */

if (!customElements.get('pack-builder')) {
  class PackBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.inputs = Array.from(this.querySelectorAll('input[data-variant-id]'));
      this.addBtn = this.querySelector('[data-add]');
      this.label = this.querySelector('[data-label]');
      this.checkoutBtn = this.querySelector('[data-checkout]');
      this.checkoutLabel = this.querySelector('[data-checkout-label]');
      this.totalEl = this.querySelector('[data-total]');
      this.countEl = this.querySelector('[data-count]');
      this.errorEl = this.querySelector('[data-error]');
      this.savingsEl = this.querySelector('[data-savings]');

      // Envío gratis: umbral en céntimos, igual que dataset.price.
      this.shipEl = this.querySelector('[data-ship]');
      this.shipMsg = this.querySelector('[data-ship-msg]');
      this.shipRest = this.querySelector('[data-ship-rest]');
      this.shipBar = this.querySelector('[data-ship-bar]');
      this.shipFill = this.querySelector('[data-ship-fill]');
      this.shipKnob = this.querySelector('[data-ship-knob]');
      this.threshold = this.shipEl ? Number(this.shipEl.dataset.threshold) || 0 : 0;

      // Estado inicial: qué variante viene elegida y con qué cantidad.
      this.defaultVariantId = this.dataset.defaultVariant || '';
      this.defaultQty = Math.max(1, parseInt(this.dataset.defaultQty, 10) || 1);

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('input', event => {
        const input = event.target;
        if (!input.matches('input[data-variant-id]')) return;
        if (this.dataset.mode === 'bundle' && input.hasAttribute('data-main') && Number(input.value) > 0) {
          this.querySelectorAll('[data-main]').forEach(el => { if (el !== input) el.value = 0; });
          input.closest('[data-product-row]').querySelector('[data-main-choice]').checked = true;
        }
        this.syncAddonCheck(input);
        this.refresh();
      });
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.dataset.mode === 'bundle') {
        const first = this.querySelector('[data-main-choice]');
        if (first && !this.querySelector('[data-main-choice]:checked')) {
          first.checked = true;
          first.closest('[data-product-row]').querySelector('[data-main]').value = 1;
        }
      }
      this.refresh();
      this.renderTabPrices();
    }

    // Barra de progreso hacia el envío gratis. Es el único "descuento" que
    // el pedido puede desbloquear, así que tiene que verse antes del botón.
    updateShip(total) {
      if (!this.shipEl || !this.threshold) return;

      const free = total >= this.threshold;
      const rest = Math.max(0, this.threshold - total);
      const pct = Math.min(100, (total / this.threshold) * 100);

      this.shipEl.classList.toggle('is-free', free);
      this.shipEl.classList.toggle('is-progress', total > 0 && !free);
      this.shipEl.classList.toggle('is-quiet', total <= 0);

      if (this.shipFill) this.shipFill.style.width = pct.toFixed(1) + '%';
      if (this.shipKnob) this.shipKnob.style.left = pct.toFixed(1) + '%';
      if (this.shipBar) {
        this.shipBar.setAttribute('aria-valuenow', String(Math.round(Math.min(total, this.threshold) / 100)));
      }
      if (this.shipMsg) this.shipMsg.textContent = 'Envío gratis';
      if (this.shipRest) {
        // El monto vive en la píldora: el mensaje queda en una sola línea
        // incluso en la columna angosta del producto.
        this.shipRest.textContent = free
          ? '✓ incluido'
          : total <= 0
            ? 'desde ' + this.formatMoney(this.threshold)
            : 'faltan ' + this.formatMoney(rest);
        this.shipRest.hidden = false;
      }

      // Un solo gesto al cruzar el umbral, no en cada tecla.
      if (free && this.shipWasFree === false) {
        this.shipEl.classList.add('is-just-free');
        clearTimeout(this.shipPopTimer);
        this.shipPopTimer = setTimeout(() => this.shipEl.classList.remove('is-just-free'), 650);
      }
      this.shipWasFree = free;
    }

    // Precio "desde" en cada pestaña de formato: la primera decisión de la
    // página no debería obligar a cambiar de pestaña para saber cuánto cuesta.
    renderTabPrices() {
      this.querySelectorAll('[data-panel]').forEach((panel) => {
        const tab = this.querySelector('[data-tab="' + panel.dataset.panel + '"]');
        const target = tab ? tab.querySelector('[data-tab-price]') : null;
        if (!target) return;
        const prices = Array.from(panel.querySelectorAll('input[data-variant-id]'))
          .map((input) => Number(input.dataset.price))
          .filter((price) => price > 0);
        target.textContent = prices.length ? 'desde ' + this.formatMoney(Math.min.apply(null, prices)) : '';
      });
    }

    normalize(input) {
      const max = input.max === '' ? 999 : Math.max(0, Number(input.max) || 0);
      const value = Math.max(0, Math.min(max, Math.floor(Number(input.value) || 0)));
      input.value = value;
      return value;
    }

    isAddon(input) {
      return !!input && input.hasAttribute('data-addon');
    }

    // Tocar la cantidad de un adicional es intención explícita: marcamos el check.
    syncAddonCheck(input) {
      if (!this.isAddon(input)) return;
      const row = input.closest('[data-product-row]');
      const check = row ? row.querySelector('[data-addon-check]') : null;
      if (!check) return;
      check.checked = Number(input.value) > 0;
    }

    onChange(event) {
      const target = event.target;
      const row = target.closest('[data-product-row]');
      if (!row) return;
      const input = row.querySelector('input[data-variant-id]');
      if (target.matches('[data-addon-check]')) {
        // El check decide si el adicional entra o no. La cantidad se conserva
        // siempre en 1 como mínimo: marcar tiene que ser un solo toque.
        if (Number(input.value) < 1) input.value = 1;
      }
      if (target.matches('[data-main-choice]')) {
        this.querySelectorAll('[data-main]').forEach(el => { el.value = el === input ? 1 : 0; });
      }
      if (target.matches('[data-product-variant]')) {
        const option = target.selectedOptions[0];
        input.dataset.variantId = option.value;
        ['price', 'compare', 'title'].forEach(key => { input.dataset[key] = option.dataset[key]; });
        input.max = option.dataset.max;
        row.querySelector('[data-row-price]').innerHTML = this.formatMoney(Number(option.dataset.price));
      }
      if (target.matches('[data-main-choice]') || target.matches('[data-product-variant]')) {
        if (Number(input.value) > 0) this.syncGallery(this.rowMediaId(input));
      }
      this.refresh();
    }

    onKeydown(event) {
      const tab = event.target.closest('[data-tab]');
      if (!tab || !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const tabs = Array.from(this.querySelectorAll('[data-tab]'));
      let index = tabs.indexOf(tab);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      this.switchTab(tabs[index].dataset.tab);
      tabs[index].focus();
    }

    onClick(event) {
      if (this.busy) return;

      const step = event.target.closest('[data-step]');
      if (step) {
        const input = step.parentElement.querySelector('input');
        const next = (parseInt(input.value, 10) || 0) + parseInt(step.dataset.step, 10);
        input.value = Math.max(0, next);
        const row = input.closest('[data-product-row]');
        if (row && input.hasAttribute('data-main') && Number(input.value) > 0) {
          this.querySelectorAll('[data-main]').forEach(el => { if (el !== input) el.value = 0; });
          row.querySelector('[data-main-choice]').checked = true;
        }
        this.syncAddonCheck(input);
        if (Number(input.value) > 0) this.syncGallery(this.rowMediaId(input));
        this.refresh();
        return;
      }

      const tab = event.target.closest('[data-tab]');
      if (tab) {
        this.switchTab(tab.dataset.tab);
        return;
      }

      // Toda la fila del adicional es un objetivo táctil: tocar el nombre o la
      // foto marca/desmarca. El stepper, el selector de variante y el propio
      // check quedan fuera para no robarles el clic.
      const addonRow = event.target.closest('.pack__product-row');
      if (addonRow && !event.target.closest('.pack__qty, select, .pack__variant, label, a, .pack__choice')) {
        const check = addonRow.querySelector('[data-addon-check]');
        if (check) {
          check.checked = !check.checked;
          check.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }

      if (event.target.closest('[data-add]')) {
        this.addToCart();
        return;
      }
      if (event.target.closest('[data-checkout]')) this.addToCart({ checkout: true });
    }

    switchTab(id) {
      let mediaId = null;

      this.querySelectorAll('[data-tab]').forEach((tab) => {
        const active = tab.dataset.tab === id;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active) mediaId = tab.dataset.mediaId;
      });

      this.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.classList.toggle('pack--hidden', panel.dataset.panel !== id);
      });

      if (this.dataset.syncGallery === 'true') this.syncGallery(mediaId);
    }

    // Mueve la galería principal a la foto del formato elegido.
    // Envuelto en try/catch: si el tema cambia su API, el selector
    // sigue funcionando aunque la galería no se mueva.
    // El id de la galería es `MediaGallery-<sectionId>`: es la fuente más
    // fiable del section id, porque el tema no siempre expone data-section.
    gallerySectionId(gallery) {
      const fromId = (gallery.id || '').replace(/^MediaGallery-/, '');
      if (fromId && fromId !== gallery.id) return fromId;

      const host = gallery.closest('[data-section]') || gallery.closest('product-info');
      return gallery.dataset.section || (host && (host.dataset.section || host.dataset.sectionId)) || '';
    }

    // Sin `prepend`: mover la diapositiva al principio reordena la galería
    // en cada clic y descoloca las miniaturas. Sólo la activamos y saltamos.
    syncGallery(mediaId) {
      if (!mediaId) return;

      const gallery = (this.closest('product-info') || document).querySelector('media-gallery');
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

      const sectionId = this.gallerySectionId(gallery);
      if (!sectionId) return;

      // setActiveMedia() de Dawn arrastra la ventana hasta la galería cuando
      // la imagen queda fuera de pantalla. En móvil el pack está muy abajo:
      // cada toque devolvía al cliente al inicio de la página. Silenciamos el
      // scroll de la ventana durante esa llamada (la foto cambia igual) y
      // devolvemos la posición exacta por si algo se movió.
      const scrollToOriginal = window.scrollTo;
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      window.scrollTo = () => {};
      try {
        gallery.setActiveMedia(sectionId + '-' + mediaId, false);
      } catch (error) {
        /* la galería de este tema usa otra API; no es crítico */
      } finally {
        window.setTimeout(() => {
          window.scrollTo = scrollToOriginal;
          if (Math.abs(window.scrollY - scrollY) > 2) {
            scrollToOriginal.call(window, { top: scrollY, left: scrollX, behavior: 'instant' });
          }
        }, 80);
      }
    }

    rowMediaId(input) {
      const row = input.closest('.pack__row');
      return row ? row.dataset.mediaId : null;
    }

    getItems() {
      return this.inputs
        .map((input) => {
          const price = Number(input.dataset.price);
          const row = input.closest('[data-product-row]');
          const check = row ? row.querySelector('[data-addon-check]') : null;
          const quantity = this.normalize(input);
          return {
            id: Number(input.dataset.variantId),
            // El adicional muestra su cantidad pero sólo suma si está marcado.
            quantity: check && !check.checked ? 0 : quantity,
            title: input.dataset.title || input.getAttribute('aria-label'),
            price: price,
            compare: Math.max(Number(input.dataset.compare) || 0, price)
          };
        })
        .filter((item) => item.quantity > 0);
    }

    refresh() {
      const items = this.getItems();
      const units = items.reduce((sum, item) => sum + item.quantity, 0);
      const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const compare = items.reduce((sum, item) => sum + item.compare * item.quantity, 0);
      const ahorro = compare - total;

      const rendered = this.formatMoney(total);
      if (rendered !== this.lastTotal) {
        this.totalEl.innerHTML = rendered;
        this.bump();
        this.lastTotal = rendered;
      }
      this.totalEl.classList.toggle('pack__total--zero', units === 0);

      if (this.savingsEl) {
        this.savingsEl.innerHTML = ahorro > 0 ? 'Ahorras ' + this.formatMoney(ahorro) : '';
        this.savingsEl.hidden = ahorro <= 0;
      }

      this.countEl.textContent =
        units === 0 ? 'Sin unidades' : units + (units === 1 ? ' unidad' : ' unidades');

      this.updateShip(total);

      this.setButtonState(this.addBtn, this.label, units === 0, rendered);
      this.setButtonState(this.checkoutBtn, this.checkoutLabel, units === 0, rendered);

      this.markSelection();
      const detail = this.querySelector('[data-selection]');
      const list = this.querySelector('[data-selection-list]');
      if (detail && list) {
        detail.hidden = items.length === 0;
        list.replaceChildren();
        items.forEach(item => {
          const line = document.createElement('li');
          const name = document.createElement('span');
          const price = document.createElement('strong');
          name.textContent = item.quantity + ' × ' + item.title;
          price.innerHTML = this.formatMoney(item.price * item.quantity);
          line.append(name, price);
          list.appendChild(line);
        });
      }
      this.querySelectorAll('[data-product-row]').forEach(row => {
        const input = row.querySelector('input[data-variant-id]');
        const checkbox = row.querySelector('[data-addon-check]');
        if (!checkbox || !input) return;
        if (Number(input.value) === 0) checkbox.checked = false;
        row.classList.toggle('is-added', checkbox.checked);
      });
      this.querySelectorAll('[data-step]').forEach(button => {
        const input = button.parentElement.querySelector('input');
        button.disabled = this.busy || (Number(button.dataset.step) < 0 ? Number(input.value) <= 0 : Number(input.value) >= Number(input.max || 999));
      });
    }

    // Sin selección el botón NO se deshabilita: apagado se leía como
    // "agotado". Queda activo y el texto explica qué falta. Con selección el
    // botón lleva el monto: nadie tiene que adivinar cuánto se le va a cobrar.
    setButtonState(button, labelEl, empty, priceHtml) {
      if (!button || !labelEl) return;
      button.disabled = !!this.busy;
      if (this.busy) return;
      button.setAttribute('aria-disabled', empty ? 'true' : 'false');

      if (empty) {
        labelEl.textContent = this.dataset.emptyLabel || 'Elige tus productos';
        return;
      }

      labelEl.textContent = button.dataset.fullLabel || labelEl.textContent;
      if (priceHtml) {
        const price = document.createElement('span');
        price.className = 'pack__cta-price';
        price.textContent = priceHtml;
        labelEl.append(' ', price);
      }
    }

    notice(text, hint) {
      if (!this.errorEl) return;
      this.errorEl.textContent = text;
      this.errorEl.classList.toggle('pack__error--hint', !!hint);
      this.errorEl.hidden = false;
      clearTimeout(this.noticeTimer);
      if (hint) {
        this.noticeTimer = setTimeout(() => {
          this.errorEl.hidden = true;
        }, 4000);
      }
    }

    // Resalta la fila elegida y pone el contador en la pestaña, para que
    // al saltar entre formatos no se pierda de vista lo ya seleccionado.
    markSelection() {
      this.inputs.forEach((input) => {
        const row = input.closest('.pack__row');
        if (!row) return;
        // Los adicionales se pintan con .is-added (manda el check), no con
        // .is-selected: si no, una fila sin marcar se veía "elegida".
        if (this.isAddon(input)) {
          row.classList.remove('is-selected');
          return;
        }
        row.classList.toggle('is-selected', (parseInt(input.value, 10) || 0) > 0);
      });

      this.querySelectorAll('[data-panel]').forEach((panel) => {
        const units = Array.from(panel.querySelectorAll('input[data-variant-id]')).reduce(
          (sum, input) => sum + (parseInt(input.value, 10) || 0),
          0
        );
        const tab = this.querySelector('[data-tab="' + panel.dataset.panel + '"]');
        if (!tab) return;

        const badge = tab.querySelector('[data-tab-badge]');
        if (badge) {
          badge.textContent = units;
          badge.hidden = units === 0;
        }
      });
    }

    bump() {
      if (!this.totalEl) return;
      this.totalEl.classList.remove('is-bumped');
      void this.totalEl.offsetWidth; // reinicia la animación
      this.totalEl.classList.add('is-bumped');
      clearTimeout(this.bumpTimer);
      this.bumpTimer = setTimeout(() => this.totalEl.classList.remove('is-bumped'), 180);
    }

    // Después de agregar volvemos al estado por defecto (1 unidad del formato
    // preseleccionado) y no a cero: una caja en $0 invita a irse.
    resetSelection() {
      const mainInputs = this.inputs.filter((input) => input.hasAttribute('data-main'));
      const target = this.defaultVariantId
        ? mainInputs.find((input) => input.dataset.variantId === this.defaultVariantId)
        : (this.dataset.mode === 'bundle' ? mainInputs[0] : null);

      this.inputs.forEach((input) => {
        if (!input.hasAttribute('data-main')) {
          input.value = 1;
          return;
        }
        input.value = input === target ? this.defaultQty : 0;
      });

      this.querySelectorAll('[data-addon-check]').forEach((check) => { check.checked = false; });
      this.refresh();
    }

    // Replica el filtro `money` de Liquid para que el total use exactamente
    // el mismo formato que los precios de cada fila. Con Intl salía
    // "20.392 CLP" arriba y "$20.392" abajo: se ve como un error.
    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const value = Math.abs(cents) / 100;

      const build = (decimals, thousands, decimal) => {
        const parts = value.toFixed(decimals).split('.');
        const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return decimals > 0 ? whole + decimal + parts[1] : whole;
      };

      const rules = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_no_decimals_with_space_separator: [0, ' ', '.'],
        amount_with_space_separator: [2, ' ', ','],
        amount_with_period_and_space_separator: [2, ' ', '.']
      };

      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, name) =>
        build.apply(null, rules[name] || rules.amount)
      );
    }

    async addToCart(options) {
      if (this.busy) return;
      const goToCheckout = !!(options && options.checkout);
      const grouped = new Map();
      this.getItems().forEach(item => grouped.set(item.id, (grouped.get(item.id) || 0) + item.quantity));
      const items = Array.from(grouped, ([id, quantity]) => ({ id, quantity }));
      if (this.querySelector('[data-main]') && !this.inputs.some(input => input.hasAttribute('data-main') && Number(input.value) > 0)) {
        this.notice('Elige tu producto o formato antes de añadir complementos.', true);
        return;
      }

      if (!items.length) {
        this.notice('Elige al menos un producto para continuar.', true);
        const input = this.querySelector('[data-panel]:not(.pack--hidden) input[data-variant-id]');
        if (input) input.focus();
        return;
      }

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const drawer = document.querySelector('cart-drawer');

      const sections =
        drawer && typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map((section) => section.id)
          : ['cart-icon-bubble'];

      const button = goToCheckout ? this.checkoutBtn : this.addBtn;
      const labelEl = goToCheckout ? this.checkoutLabel : this.label;

      this.errorEl.hidden = true;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('button, input, select').forEach(control => {
        control.dataset.packDisabled = control.disabled ? 'true' : 'false';
        control.disabled = true;
      });
      button.dataset.loading = 'true';
      labelEl.textContent = goToCheckout ? 'Yendo al pago…' : 'Agregando…';

      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: items,
            sections: sections,
            sections_url: window.location.pathname
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(this.friendlyError(data));
        }

        if (goToCheckout) {
          window.location.href = root + 'checkout';
          return;
        }

        this.resetSelection();

        try {
          this.showCart(root, data, drawer);
        } catch (renderError) {
          // The items were added successfully. Do not encourage a duplicate retry.
          this.confirm(root);
        }
      } catch (error) {
        this.notice(error.message || 'No pudimos conectar con el carrito. Inténtalo de nuevo.');
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        this.querySelectorAll('[data-pack-disabled]').forEach(control => {
          control.disabled = control.dataset.packDisabled === 'true';
          delete control.dataset.packDisabled;
        });
        delete button.dataset.loading;
        this.refresh(); // repone los textos de ambos botones
      }
    }

    showCart(root, data, drawer) {
      if (!drawer) {
        // Carrito tipo página o notificación: no sacamos a la persona del producto.
        this.renderSection(data, 'cart-icon-bubble');
        this.confirm(root);
        return;
      }

      if (typeof drawer.renderContents === 'function') {
        drawer.renderContents(data);
      }

      // Dawn imprime <cart-drawer class="drawer is-empty"> cuando la página se
      // cargó con el carrito vacío, y renderContents solo reemplaza el interior.
      // Sin esta limpieza el cajón abre en blanco aunque los productos ya estén.
      drawer.classList.remove('is-empty');
      drawer.querySelectorAll('.drawer__inner.is-empty').forEach((el) => {
        el.classList.remove('is-empty');
      });

      this.renderSection(data, 'cart-icon-bubble');

      if (typeof drawer.open === 'function') drawer.open();

      // Avisamos al resto del tema: barras de envío gratis, contadores, apps.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pack-builder', cartData: data });
      }
    }

    // Shopify rechaza el lote completo si una sola variante no alcanza stock.
    friendlyError(data) {
      const left = typeof data.items_left === 'number' ? data.items_left : null;
      if (left !== null) {
        const name = data.title || 'ese formato';
        return left === 0
          ? 'Se agotó ' + name + ' mientras armabas el pack.'
          : 'Solo quedan ' + left + ' unidades de ' + name + '. Ajusta la cantidad e inténtalo de nuevo.';
      }
      return data.description || data.message || 'No se pudo agregar al carrito.';
    }

    renderSection(data, id) {
      const html = data.sections && data.sections[id];
      const target = document.getElementById(id);
      if (!html || !target) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('.shopify-section') || parsed.body;
      target.innerHTML = source.innerHTML;
    }

    confirm(root) {
      let note = this.querySelector('[data-confirm]');
      if (!note) {
        note = document.createElement('p');
        note.className = 'pack__confirm';
        note.setAttribute('data-confirm', '');
        this.querySelector('.pack__footer').appendChild(note);
      }
      note.innerHTML = 'Agregado al carrito. <a href="' + root + 'cart">Ver carrito</a>';
      note.hidden = false;

      clearTimeout(this.confirmTimer);
      this.confirmTimer = setTimeout(function () {
        note.hidden = true;
      }, 6000);
    }
  }

  customElements.define('pack-builder', PackBuilder);
}